/**
 * NotifyWhenDoneButton
 *
 * Shows a "Notify me when finished" button after 10 seconds of waiting.
 * On click, subscribes the user for a notification when the job completes.
 *
 * Props:
 *   jobType    - "evaluation" | "comparison" | "analysis"
 *   sessionId  - the session ID for the running job
 *   startedAt  - Date or timestamp when the job started (used to delay visibility)
 */
import { useState, useEffect } from "react";
import { BellRinging, Check } from "@phosphor-icons/react";
import { subscribeJobNotification } from "../../api/notifications";

const SHOW_DELAY_MS = 10_000; // 10 seconds

export default function NotifyWhenDoneButton({ jobType, sessionId, startedAt }) {
  const [visible, setVisible] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!startedAt) return;

    const elapsed = Date.now() - new Date(startedAt).getTime();
    if (elapsed >= SHOW_DELAY_MS) {
      setVisible(true);
      return;
    }

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS - elapsed);
    return () => clearTimeout(timer);
  }, [startedAt]);

  // Reset subscribed state if sessionId changes (new job)
  useEffect(() => {
    setSubscribed(false);
    setVisible(false);
  }, [sessionId]);

  const handleClick = async () => {
    if (!sessionId || subscribed || loading) return;
    setLoading(true);
    try {
      await subscribeJobNotification(jobType, sessionId);
      setSubscribed(true);
    } catch {
      // Fail silently — button stays clickable
    } finally {
      setLoading(false);
    }
  };

  if (!visible || !sessionId) return null;

  if (subscribed) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-emerald-600 font-medium mt-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md"
        data-testid="notify-subscribed-confirmation"
      >
        <Check size={14} weight="bold" />
        You'll be notified when this finishes
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="flex items-center gap-2 text-xs font-medium mt-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-slate-600 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 transition-colors disabled:opacity-50"
      data-testid="notify-when-done-button"
    >
      <BellRinging size={14} weight="fill" className={loading ? "animate-pulse" : ""} />
      {loading ? "Subscribing…" : "Notify me when finished"}
    </button>
  );
}
